import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import { useAuth } from "../App";
import { Bell, Check, Archive, CheckCircle2, AlertTriangle, XCircle, RefreshCw, ClipboardCheck, Clock, Filter, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_ICONS = {
  "client_onboarding.run_assigned": { icon: ClipboardCheck, color: "#36A9E1", label: "Run Assigned" },
  "client_onboarding.response_submitted": { icon: Clock, color: "#38BDF8", label: "Response Submitted" },
  "client_onboarding.revision_requested": { icon: RefreshCw, color: "#FBBF24", label: "Revision Requested" },
  "client_onboarding.submission_accepted": { icon: CheckCircle2, color: "#4ADE80", label: "Accepted" },
  "client_onboarding.submission_rejected": { icon: XCircle, color: "#F87171", label: "Rejected" },
  "governance.assessment_created": { icon: AlertTriangle, color: "#A78BFA", label: "Assessment" },
  "governance.remediation_overdue": { icon: AlertTriangle, color: "#F87171", label: "Overdue" },
};

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "read", label: "Read" },
  { value: "archived", label: "Archived" },
];

export default function NotificationsPage() {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (statusFilter) params.append("status", statusFilter);
      const res = await fetch(`${API}/api/notifications/my-feed?${params}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [statusFilter, offset]);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/notifications/unread-count`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchFeed(); fetchUnread(); }, [fetchFeed, fetchUnread]);

  const markRead = async (eventId) => {
    await fetch(`${API}/api/notifications/${eventId}/read`, { method: "PATCH", credentials: "include" });
    setEvents(prev => prev.map(e => e.event_id === eventId ? { ...e, status: "read" } : e));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const archiveEvent = async (eventId) => {
    await fetch(`${API}/api/notifications/${eventId}/archive`, { method: "PATCH", credentials: "include" });
    setEvents(prev => prev.filter(e => e.event_id !== eventId));
    setTotal(prev => prev - 1);
  };

  const markAllRead = async () => {
    await fetch(`${API}/api/notifications/mark-all-read`, { method: "POST", credentials: "include" });
    setEvents(prev => prev.map(e => ({ ...e, status: "read" })));
    setUnreadCount(0);
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <QMSLayout title="Notifications">
      <div className="space-y-5" data-testid="notifications-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <p className="text-[#8B95A5]">Your notification feed and activity history</p>
          <div className="flex items-center gap-3">
            <div className="flex rounded overflow-hidden border border-[rgba(255,255,255,0.1)]">
              {STATUS_FILTERS.map(f => (
                <button
                  key={f.value}
                  onClick={() => { setStatusFilter(f.value); setOffset(0); }}
                  className={`px-3 py-1.5 text-xs transition-colors ${statusFilter === f.value ? "bg-[#36A9E1] text-white" : "bg-[#1F222D] text-[#8B95A5] hover:text-white"}`}
                  data-testid={`filter-${f.value || "all"}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs px-3 py-1.5 rounded bg-[#36A9E1]/10 text-[#36A9E1] hover:bg-[#36A9E1]/20" data-testid="mark-all-read-page">
                <Check size={12} className="inline mr-1" /> Mark All Read ({unreadCount})
              </button>
            )}
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="notification-kpis">
          <div className="kpi-card" style={{ "--kpi-accent": "#F87171" }}>
            <p className="kpi-label">Unread</p>
            <p className="kpi-value">{unreadCount}</p>
          </div>
          <div className="kpi-card" style={{ "--kpi-accent": "#36A9E1" }}>
            <p className="kpi-label">Total</p>
            <p className="kpi-value">{total}</p>
          </div>
          <div className="kpi-card" style={{ "--kpi-accent": "#4ADE80" }}>
            <p className="kpi-label">Filter</p>
            <p className="kpi-value text-base">{statusFilter || "All"}</p>
          </div>
          <div className="kpi-card" style={{ "--kpi-accent": "#FBBF24" }}>
            <p className="kpi-label">Page</p>
            <p className="kpi-value">{currentPage}/{totalPages || 1}</p>
          </div>
        </div>

        {loading && <div className="text-center py-12 text-[#8B95A5]">Loading notifications...</div>}

        {!loading && events.length === 0 && (
          <div className="text-center py-16 text-[#8B95A5]" data-testid="empty-notifications">
            <Bell size={48} className="mx-auto mb-4 opacity-20" />
            <p>No notifications {statusFilter ? `with status "${statusFilter}"` : "yet"}</p>
          </div>
        )}

        {!loading && events.length > 0 && (
          <div className="space-y-2" data-testid="notification-list">
            {events.map(evt => {
              const meta = EVENT_ICONS[evt.event_type] || { icon: Bell, color: "#8B95A5", label: evt.event_type };
              const Icon = meta.icon;
              const isUnread = evt.status === "unread";
              return (
                <div
                  key={evt.event_id}
                  className={`qms-card transition-colors ${isUnread ? "border-l-2 border-l-[#36A9E1]" : ""}`}
                  data-testid={`notif-row-${evt.event_id}`}
                >
                  <div className="qms-card-body flex items-start gap-4">
                    <div className="shrink-0 mt-1 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${meta.color}15` }}>
                      <Icon size={18} style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-sm font-medium ${isUnread ? "text-white" : "text-[#8B95A5]"}`}>{evt.title}</span>
                        {isUnread && <span className="w-2 h-2 rounded-full bg-[#36A9E1] shrink-0" />}
                        <span className="text-[10px] px-1.5 py-0.5 rounded ml-auto shrink-0" style={{ background: `${meta.color}15`, color: meta.color }}>{meta.label}</span>
                      </div>
                      <p className="text-xs text-[#8B95A5]">{evt.message}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-[10px] text-[#555]">{formatDate(evt.created_at)}</span>
                        {evt.actor_name && <span className="text-[10px] text-[#555]">by {evt.actor_name}</span>}
                        {evt.source_entity_id && <span className="text-[10px] text-[#38BDF8] font-mono">{evt.source_entity_id}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isUnread && (
                        <button onClick={() => markRead(evt.event_id)} className="p-1.5 rounded hover:bg-[rgba(255,255,255,0.06)] text-[#8B95A5] hover:text-[#4ADE80]" title="Mark as read" data-testid={`mark-read-${evt.event_id}`}>
                          <Check size={14} />
                        </button>
                      )}
                      {evt.status !== "archived" && (
                        <button onClick={() => archiveEvent(evt.event_id)} className="p-1.5 rounded hover:bg-[rgba(255,255,255,0.06)] text-[#8B95A5] hover:text-[#FBBF24]" title="Archive" data-testid={`archive-${evt.event_id}`}>
                          <Archive size={14} />
                        </button>
                      )}
                      {evt.link && (
                        <a href={evt.link} className="p-1.5 rounded hover:bg-[rgba(255,255,255,0.06)] text-[#8B95A5] hover:text-[#36A9E1]" title="Go to source" data-testid={`link-${evt.event_id}`}>
                          <ClipboardCheck size={14} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4" data-testid="pagination">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="text-xs px-3 py-1.5 rounded bg-[#1F222D] text-[#8B95A5] hover:text-white disabled:opacity-30"
              data-testid="prev-page"
            >
              Previous
            </button>
            <span className="text-xs text-[#8B95A5]">Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={currentPage >= totalPages}
              className="text-xs px-3 py-1.5 rounded bg-[#1F222D] text-[#8B95A5] hover:text-white disabled:opacity-30"
              data-testid="next-page"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </QMSLayout>
  );
}
